/* eslint-disable import/no-unresolved */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';

import profitLossUtils from '../../utils/profitLossUtils';
import { getDailyPls } from '../../api/portfolioApi';
import { useAccountInfo } from '../../hooks/useAccountInfo';
import Logger from '../Logger';

const QUERY_DATE_FORMAT = 'YYYY-MM-DD';

const {
  getChartData,
  getInstrumentsPl,
  getTotalPl,
  getTotalRealizedSwapPl,
  getTotalUnrealizedSwapPl,
  getNumberOfEntries,
  getNumberOfExits,
  getMonthOptions,
} = profitLossUtils;

const EMPTY_MONTH_DATA = {
  chartDataRealized: [],
  chartDataUnrealized: [],
  plByInstrument: [],
  totalPl: 0,
  totalRealizedSwapPl: 0,
  totalUnrealizedSwapPl: 0,
  numberOfEntries: 0,
  numberOfExits: 0,
};

const useProfitLossFilter = (serviceId) => {
  const initialMonthOptions = getMonthOptions(moment().tz('Asia/Tokyo').format('YYYYMMDD'));
  const [byMonthDataMap, setByMonthDataMap] = useState(null);
  const [selectedMonthData, setSelectedMonthData] = useState(EMPTY_MONTH_DATA);
  const isFetchingRef = useRef(false);
  const instrumentList = useSelector((state) => state.settings.instrumentList);
  const [monthOptions, setMonthOptions] = useState(initialMonthOptions);
  const [selectedMonth, setSelectedMonth] = useState(initialMonthOptions[0]);
  const accountInfo = useAccountInfo()[serviceId];
  const [currentFrontDate, setCurrentFrontDate] = useState(null);

  const handleMonthSelect = useCallback(
    (item) => {
      if (byMonthDataMap == null) {
        return;
      }
      setSelectedMonth(item);
      setSelectedMonthData(byMonthDataMap[item.value] ?? EMPTY_MONTH_DATA);
    },
    [byMonthDataMap],
  );

  const getByMonthDataMap = useCallback(
    (data, options) => {
      const result = {};
      if (data && data.length) {
        return options.reduce((prev, month) => {
          const filtered = data.filter((item) => item.date.includes(month.value));
          const plByInstrument = getInstrumentsPl(filtered, { doSummaryInstrumentId: true, instrumentList });
          const byMonthData = {
            chartDataRealized: getChartData(filtered),
            chartDataUnrealized: getChartData(filtered, { useUnrealizedSwap: true }),
            plByInstrument,
            totalPl: getTotalPl(plByInstrument),
            totalRealizedSwapPl: getTotalRealizedSwapPl(plByInstrument),
            totalUnrealizedSwapPl: getTotalUnrealizedSwapPl(filtered),
            numberOfEntries: getNumberOfEntries(plByInstrument),
            numberOfExits: getNumberOfExits(plByInstrument),
          };
          return {
            ...prev,
            [month.value]: byMonthData,
          };
        }, result);
      }
      return result;
    },
    [instrumentList],
  );

  const isPreLoad = byMonthDataMap == null;
  const [isLoading, setIsLoading] = useState(false);
  const doFetchData = isPreLoad && !isLoading;

  const fetchPlData = useCallback(async () => {
    if (isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;
    setIsLoading(true);

    try {
      const today = moment().tz('Asia/Tokyo').format('YYYYMMDD');
      const from = moment(today, 'YYYYMMDD').subtract(1, 'year').format(QUERY_DATE_FORMAT);
      const { data: result } = await getDailyPls({
        serviceId,
        from,
      });

      // APIで取得したフロント日をベースにカレンダー表示する (取得できない場合はシステム日)
      const options = getMonthOptions(result?.currentFrontDate ?? today);
      const initialMonth = options[0];

      const dataMap = getByMonthDataMap(result.list, options);
      setByMonthDataMap(dataMap);
      setSelectedMonthData(dataMap[initialMonth.value] ?? EMPTY_MONTH_DATA);
      setMonthOptions(options);
      setSelectedMonth(initialMonth);
      setCurrentFrontDate(result?.currentFrontDate);
    } catch (e) {
      Logger.error(e);
    } finally {
      isFetchingRef.current = false;
      setIsLoading(false);
    }
  }, [getByMonthDataMap, serviceId]);

  useEffect(() => {
    if (!accountInfo.isNotAvailable && doFetchData) {
      fetchPlData();
    }
  }, [accountInfo.isNotAvailable, doFetchData, fetchPlData]);

  const resetSelectedMonth = useCallback(() => {
    if (monthOptions.length > 0) {
      handleMonthSelect(monthOptions[0]);
    }
  }, [handleMonthSelect, monthOptions]);

  return useMemo(
    () => ({
      instrumentList,
      plByInstrument: selectedMonthData.plByInstrument,
      chartDataRealized: selectedMonthData.chartDataRealized,
      chartDataUnrealized: selectedMonthData.chartDataUnrealized,
      totalPl: selectedMonthData.totalPl,
      totalRealizedSwapPl: selectedMonthData.totalRealizedSwapPl,
      totalUnrealizedSwapPl: selectedMonthData.totalUnrealizedSwapPl,
      numberOfEntries: selectedMonthData.numberOfEntries,
      numberOfExits: selectedMonthData.numberOfExits,
      monthOptions,
      handleMonthSelect,
      selectedMonth,
      setSelectedMonth,
      resetSelectedMonth,
      byMonthDataMap: byMonthDataMap || {},
      currentFrontDate,
    }),
    [
      instrumentList,
      selectedMonthData,
      monthOptions,
      handleMonthSelect,
      selectedMonth,
      setSelectedMonth,
      resetSelectedMonth,
      byMonthDataMap,
      currentFrontDate,
    ],
  );
};

export default useProfitLossFilter;
